import { Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import type { MessageAttachment, MessageItem } from '@/shared/api';
import { useApi } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { formatDateTime } from '@/shared/lib/format';
import { cameFromChannel, channelLabel } from '../inboxModel';
import { MessageBody } from './MessageBody';
import { MessageSendStatus } from './MessageSendStatus';
import { MessageSendResolutionAction } from './MessageSendResolutionAction';

/**
 * Eine Nachricht im Verlauf: System-Notiz, Gast- oder Host-Bubble.
 *
 * `min-w-0` und `wrap-anywhere` sind hier kein Feinschliff, sondern die Reparatur eines echten
 * Befunds. Der Text einer Onboarding-Nachricht enthält eine Magic-Link-URL — eine unteilbare
 * Zeichenkette von über 200 px. `break-words` (`overflow-wrap: break-word`) bricht sie zwar beim
 * Rendern um, **verkleinert aber die intrinsische Mindestbreite nicht**: die Blase wuchs auf die
 * Breite der URL, `max-w-[86%]` wurde gegen diese aufgeblähte Breite gerechnet, und weil die
 * Host-Blase `self-end` steht, lief der Überhang nach **links** aus dem Bild — dorthin, wo kein
 * Scrollen hinkommt. `overflow-wrap: anywhere` senkt die Mindestbreite mit, `min-w-0` nimmt der
 * Flexbox entlang der Kette die inhaltsbasierte Untergrenze.
 */
export function MessageBubble({
  m,
  conversationId,
  channel = null,
}: {
  m: MessageItem;
  conversationId: string;
  channel?: string | null;
}) {
  const when = formatDateTime(m.sentAt ?? m.receivedAt ?? m.occurredAt);

  if (m.sender === 'System') {
    const inquiry = parseInquiryDetails(m.metaJson);
    return (
      <div className="bg-card-alt border-amber/70 text-ink-soft wrap-anywhere min-w-0 max-w-[82%] self-center rounded-md border border-dashed px-4 py-2.5 text-[12.5px]">
        <span className="text-[9.5px] font-bold uppercase tracking-widest text-[#8a6320]">
          {inquiry ? 'Anfrage' : 'System'}
        </span>
        {m.body && <MessageBody body={m.body} className="mt-0.5" />}
        {inquiry && (
          <dl className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11.5px]">
            {inquiry.map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="text-ink-faint capitalize">{k.replace(/_/g, ' ')}</dt>
                <dd className="text-ink-soft min-w-0 text-right font-medium">{v}</dd>
              </div>
            ))}
          </dl>
        )}
        <Attachments m={m} conversationId={conversationId} />
      </div>
    );
  }

  const isHost = m.sender === 'Host';
  const fromChannel = cameFromChannel(m);
  return (
    <div
      className={cn(
        'flex min-w-0 max-w-[86%] flex-col gap-1 sm:max-w-[74%]',
        isHost ? 'items-end self-end' : 'items-start self-start',
      )}
    >
      <div
        className={cn(
          'wrap-anywhere text-ink min-w-0 rounded-[18px] px-3.5 py-2.5 text-sm leading-relaxed',
          !isHost && 'bg-card border-hairline rounded-bl-md border',
          isHost && !fromChannel && 'bg-mint/45 rounded-br-md',
          fromChannel && 'border-mint bg-card rounded-br-md border-[1.5px] border-dashed',
        )}
      >
        {m.body && <MessageBody body={m.body} />}
        <Attachments m={m} conversationId={conversationId} />
      </div>
      <div className="text-ink-faint flex min-w-0 flex-wrap items-center justify-end gap-x-2 gap-y-1 font-mono text-[11px]">
        {isHost && (
          <span>
            {fromChannel
              ? `Über ${channelLabel(channel)} gesendet · nicht aus StayOn`
              : authorLabel(m)}
          </span>
        )}
        {isHost && !fromChannel && (
          <MessageSendStatus
            status={m.sendStatus}
            reviewRequired={m.sendReviewRequired}
            error={m.sendError}
          />
        )}
        <span>{when}</span>
        {isHost && !fromChannel && m.sendStatus === 'OutcomeUnknown' && (
          <MessageSendResolutionAction messageId={m.id} />
        )}
      </div>
    </div>
  );
}

function authorLabel(m: MessageItem): string {
  return m.composedBy === 'Ai' || m.composedBy === 'AiEdited' ? 'KI über StayOn' : 'Über StayOn';
}

// Airbnb-Inquiry: meta.booking_details defensiv aus dem Roh-JSON lesen (Struktur unverifiziert) →
// flache Skalar-Felder als Anzeige-Paare. null wenn kein/kaputtes meta.
function parseInquiryDetails(metaJson: string | null): [string, string][] | null {
  if (!metaJson) return null;
  try {
    const parsed = JSON.parse(metaJson) as { booking_details?: Record<string, unknown> };
    const details = parsed?.booking_details;
    if (!details || typeof details !== 'object') return null;
    const entries = Object.entries(details)
      .filter(([, v]) => v != null && typeof v !== 'object')
      .map(([k, v]) => [k, String(v)] as [string, string]);
    return entries.length ? entries : null;
  } catch {
    return null;
  }
}

// Anhaenge: on-demand authentifiziert geladen (Blob -> Object-URL) und in neuem Tab geoeffnet,
// da ein <img src>/<a href> den Bearer-Token nicht mitschicken wuerde.
function Attachments({ m, conversationId }: { m: MessageItem; conversationId: string }) {
  const api = useApi();
  if (!m.attachments?.length) return null;

  async function open(att: MessageAttachment) {
    try {
      const blob = await api.messaging.getAttachment(conversationId, m.id, att.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error('Anhang konnte nicht geladen werden.');
    }
  }

  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {m.attachments.map((att) => (
        <button
          key={att.id}
          onClick={() => open(att)}
          className="border-line/60 bg-card-alt/60 text-ink-soft inline-flex min-h-11 items-center gap-1.5 rounded border px-2 text-[11px] sm:min-h-0 sm:py-1"
        >
          <Paperclip size={12} aria-hidden /> {att.fileName ?? 'Anhang'}
        </button>
      ))}
    </div>
  );
}
